/*
  Tasks page (/tasks) — screen 12-tasks.

  Wave 1 (ideas 1 · 2 · 3 · 4 · 8): readable 3-line cards with hover actions, only sections with work
  ("3 of 22 sections · Show 19 empty"), one colour language, origin chips, Done with Undo + "Show done".

  Wave 2 (ideas 5 · 6 · 7 · 9):
    5  focus counters (Assigned to me · Due today · Overdue · Blocked · Needs details) that filter with one
       click; Person / Project / Priority / Location chips; Group by Section / Person / Project / Due date.
       All of it lives in the URL (?focus=overdue&person=…&group=due), so a view can be sent as a link.
       Phones: counters as chips and a List grouped by due date (Overdue / Today / Blocked / Later).
    6  Board / Table / Calendar really switch (?view=); Table = DataTable with sort, sticky header, columns,
       Duration / Progress; Calendar = week / month, drag a task to another day to change its due date.
    7  one task pane docked beside the board (a sheet on smaller screens): title, status (Blocked asks why),
       pickers with search, notes, attachments, comments, "Saved · just now", Delete in ⋯.
       "New task" opens it empty — nothing is created until the task has a name.
    9  tasks with no project, person or date wait in a "Needs details" card under the board, with check
       boxes and a bulk bar (Assign, Project, Due date, Move, Delete…) — the same bar works in the Table.
  Wave 3 (idea 10): Team load — a card under the board (5 busiest people + Unassigned: load bar, Open, late /
    blocked, Done this week; the team's on-time completion of the last 8 weeks) that opens into a full Team
    view (?view=team, every person, rows unfold into their tasks; a Team segment on phones too). Drop a card
    on a person to assign it; a name opens that person's full history, whose rows open the task even when it
    closed weeks ago or the search hides it. "This week" / "Next week" in ?load=. The 8 weeks of done tasks
    behind the on-time chart load only once Board or Team is shown.
  Links that keep working: /tasks?search=… (Dashboard "Data to fix"), /tasks?task=<id> (AI "Open in Tasks").
  View only (BUG-002): a role whose Tasks level is View in Admin › Access, and Viewer accounts (canEdit(user, 'tasks')
  is false), see the same board, table, calendar and Team load without any way to change them: no New task, no
  section "Add", no quick add, no card actions or drag, no check boxes / bulk bars / row menus, no assigning in
  Team load, and the pane opens read-only. The server refuses those writes anyway (edit_level_guard('tasks')).

  Typed work in the pane that is not saved yet (a new task's draft, notes being edited, a comment, a blocked
  reason) is guarded: closing the pane, opening another task, "New task" and "Next ›" ask "Discard …?" first,
  and so do links and reloads (useUnsavedChanges). Cancelled tasks count as closed: off the board and out of
  every counter, shown faded with "Show done".
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  Columns3,
  Eye,
  EyeOff,
  Inbox,
  ListChecks,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  SearchX,
  Settings2,
  SlidersHorizontal,
  Table2,
  Users,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { formatSortParam, parseSortParam } from '@/lib/table';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit } from '@/lib/permissions';
import { useUrlState } from '@/hooks/useUrlState';
import { useIsPhone, useMediaQuery } from '@/hooks/useMediaQuery';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { Button } from '@/components/ui/button';
import { ColumnsMenu } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterBar } from '@/components/ui/filter-bar';
import { PageHeader } from '@/components/ui/page-header';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Segmented } from '@/components/ui/segmented';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import NeedsDetailsCard from './NeedsDetailsCard';
import { BoardSkeleton, StatusLegend, TaskLanes, TaskList } from './TaskBoard';
import TaskCalendar from './TaskCalendar';
import TaskFocusCounters from './TaskFocusCounters';
import TaskPane from './TaskPane';
import TaskTable, { useTaskColumns } from './TaskTable';
import TeamLoad from './TeamLoad';
import TeamMemberTaskHistory from './TeamMemberTaskHistory';
import {
  applyTaskFilters,
  assignPatch,
  buildFilterOptions,
  buildLanes,
  completedSince,
  FOCUS_KEYS,
  FOCUS_OPTIONS,
  focusSummary,
  GROUP_KEYS,
  GROUP_OPTIONS,
  groupTasks,
  isCancelled,
  isClosed,
  isDone,
  isoDate,
  matchesFocus,
  needsDetails,
  NO_SECTION,
  normalizeStatus,
  onTimeWeeks,
  projectLabel,
  sectionOf,
  summarizeNames,
  taskAssignees,
  teamLoad,
  weekBounds,
  weekStartIso,
} from './taskModel';

const DONE_LIST_LIMIT = 500;
const DEFAULT_SORT = { key: 'due', dir: 'asc' };
const URL_DEFAULTS = {
  search: '',
  view: '',
  group: '',
  focus: '',
  person: [],
  project: [],
  priority: [],
  location: [],
  done: false,
  cal: 'week',
  at: '',
  sort: '',
  task: '',
  load: '', // Team load period: '' = this week, 'next'
};
const FILTER_KEYS = ['person', 'project', 'priority', 'location'];

// Phones: a small control keeps its look but answers taps in a 44px-high area (centred, at least 44px wide).
const TAP_AREA = "relative after:absolute after:left-1/2 after:top-1/2 after:h-11 after:w-[calc(100%+8px)] after:min-w-[44px] after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']";
// Phones: the view switch's segments are 34px (the mobile design) and answer taps 44px high.
const PHONE_SEGMENTS = "overflow-visible [&>button]:relative [&>button]:h-[34px] [&>button]:after:absolute [&>button]:after:inset-x-0 [&>button]:after:-inset-y-[5px] [&>button]:after:content-['']";

const sectionValue = (section) => (section === NO_SECTION ? '' : section);
const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
const viewLabel = (Icon, text, { compact = false } = {}) => (
  <span className="inline-flex items-center gap-1.5">
    {/* phones: four segments share the width — below 430px the words alone fit */}
    <Icon className={cn('h-3.5 w-3.5', compact && 'max-[429px]:hidden')} aria-hidden="true" />{text}
  </span>
);

function SectionsMenu({ sections, counts, showEmpty, onToggleEmpty, onAdd, onRename, onDelete }) {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const add = async (event) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value || busy) return;
    setBusy(true);
    try { if (await onAdd(value)) setDraft(''); }
    finally { setBusy(false); }
  };
  const rename = async (event) => {
    event.preventDefault();
    const value = editing?.value.trim();
    if (!value || busy) return;
    if (sections.some((section) => section !== editing.section && section.toLocaleLowerCase() === value.toLocaleLowerCase())) {
      setError('A section with this name already exists');
      return;
    }
    setBusy(true);
    setError('');
    try { if (await onRename(editing.section, value)) setEditing(null); }
    finally { setBusy(false); }
  };
  const remove = async (section) => {
    if (busy) return;
    setBusy(true);
    try { await onDelete(section); }
    finally { setBusy(false); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" data-testid="tasks-sections-menu">
          <Settings2 aria-hidden="true" /> Sections
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-[min(360px,calc(100vw-24px))] rounded-xl border-line bg-surface p-0 text-ink shadow-overlay" onEscapeKeyDown={editing ? (event) => { if (!busy) { event.preventDefault(); setEditing(null); setError(''); } } : undefined}>
        <div className="flex items-center justify-between px-3 pb-1 pt-3">
          <span className="text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3">Sections · {sections.length}</span>
          <button type="button" onClick={onToggleEmpty} className="text-[12px] font-semibold text-brand-ink hover:underline">
            {showEmpty ? 'Hide empty' : 'Show empty'}
          </button>
        </div>
        <ul className="max-h-[300px] overflow-y-auto px-1.5 py-1">
          {sections.map((section) => (
            <li key={section} className="flex min-h-9 items-center gap-1 rounded-lg px-2 text-[13px] text-ink" data-testid={`task-section-row-${section}`}>
              {editing?.section === section ? (
                <form onSubmit={rename} className="flex min-w-0 flex-1 flex-wrap items-center gap-1 py-1">
                  <input autoFocus dir="auto" aria-label="Section name" value={editing.value}
                    onChange={(event) => { setEditing({ ...editing, value: event.target.value }); setError(''); }}
                    maxLength={80} disabled={busy}
                    className="h-8 min-w-0 flex-1 rounded-lg border border-line bg-surface px-2 text-start outline-none focus:border-[color:var(--brand-soft-border)]" />
                  <Button type="submit" size="sm" disabled={busy || !editing.value.trim() || editing.value.trim() === section}>Save</Button>
                  <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => { setEditing(null); setError(''); }}>Cancel</Button>
                  {error ? <p role="alert" className="w-full text-[12px] text-status-red-ink">{error}</p> : null}
                </form>
              ) : (
                <>
                  <span dir="auto" className="min-w-0 flex-1 truncate text-start" title={section}>{section}</span>
                  <span className={cn('mr-1 text-[12px] tabular-nums', counts.get(section) ? 'font-bold text-ink-2' : 'text-ink-4')}>{counts.get(section) || 0}</span>
                  {onRename ? <Button type="button" variant="ghost" size="icon-xs" disabled={busy || Boolean(editing)}
                    aria-label={`Rename section ${section}`} title="Rename section"
                    onClick={() => { setEditing({ section, value: section }); setError(''); }}><Pencil aria-hidden="true" /></Button> : null}
                  {onDelete ? <Button type="button" variant="ghost" size="icon-xs" disabled={busy || Boolean(editing)}
                    className="text-status-red-ink" aria-label={`Delete section ${section}`} title="Delete section"
                    onClick={() => remove(section)}><Trash2 aria-hidden="true" /></Button> : null}
                </>
              )}
            </li>
          ))}
        </ul>
        {onAdd ? (
          <form onSubmit={add} className="flex items-center gap-2 border-t border-line-2 p-2">
            <input dir="auto" value={draft} onChange={(event) => setDraft(event.target.value)}
              placeholder="New section name" aria-label="New section name" maxLength={80} disabled={busy}
              className="h-8 min-w-0 flex-1 rounded-lg border border-line bg-surface px-2.5 text-start text-[13px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-soft-border)] focus:shadow-focus" />
            <Button type="submit" size="sm" disabled={!draft.trim() || busy || Boolean(editing)}>Add</Button>
          </form>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export default function PendingTasksBoard() {
  const confirm = useConfirm();
  const { user } = useAuth() || {};
  // BUG-002: View only for Tasks (Admin › Access) or a Viewer account → every write control is hidden.
  const readOnly = !canEdit(user, 'tasks');
  const isPhone = useIsPhone();
  const wide = useMediaQuery('(min-width: 1280px)');
  const [f, setF] = useUrlState(URL_DEFAULTS);

  const [tasks, setTasks] = useState([]);
  const [doneTasks, setDoneTasks] = useState([]); // everything done this week (the chip's "N this week")
  const [doneWeekLoaded, setDoneWeekLoaded] = useState(false); // the first answer for this week's done list came
  // Done tasks of the 7 weeks before this one (Team load's on-time chart; this week's come from doneTasks).
  const [doneHistory, setDoneHistory] = useState([]);
  const [doneHistoryLoading, setDoneHistoryLoading] = useState(true);
  // Loaded only once Board or Team shows the chart; every full reload (Try again) asks again.
  const [doneHistoryEpoch, setDoneHistoryEpoch] = useState(0);
  const doneHistoryFetchedRef = useRef(-1);
  // Team load › name → full history panel. The person stays set while the panel slides out (its content
  // does not blank mid-animation); the panel itself never shows one person's list under another's name.
  const [historyMember, setHistoryMember] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Tasks opened from a person's history that the board's lists do not hold (closed before this Monday, or
  // hidden by the search): kept by id so the pane can show them and changes made there stay in step.
  const [extraTasks, setExtraTasks] = useState({});
  const extraTasksRef = useRef(extraTasks);
  extraTasksRef.current = extraTasks;
  const [doneMatchIds, setDoneMatchIds] = useState(null); // while searching: ids of this week's done tasks that match
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [options, setOptions] = useState({ sections: [], locations: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const [draft, setDraft] = useState(null); // { prefill, key } while "New task" is open in the pane
  const [fillMode, setFillMode] = useState(false); // "Fill in one by one" (Needs details)
  const [tableSelection, setTableSelection] = useState([]);
  const [phoneFilters, setPhoneFilters] = useState(false);
  // Tasks added during this visit stay in the lane they were added to (a quick "Add task" has only a name);
  // if they still have no project, person or date they join "Needs details" on the next visit.
  const [freshIds, setFreshIds] = useState(() => new Set());
  const markFresh = (id) => setFreshIds((current) => new Set(current).add(id));
  const [drag, setDrag] = useState({ taskId: null, section: null, beforeId: null });
  const [paneUnsaved, setPaneUnsaved] = useState(''); // e.g. 'your comment' while the pane holds typed work
  const searchValueRef = useRef(f.search);
  const lastSearchRef = useRef('');
  const loadRequestRef = useRef(0);
  const tasksRequestRef = useRef(0);
  const doneRequestRef = useRef(0);
  const doneMatchRequestRef = useRef(0);
  const doneHistoryRequestRef = useRef(0);
  const rootRef = useRef(null);
  searchValueRef.current = f.search;

  // ---- view state from the URL ----
  const viewKeys = isPhone ? ['list', 'board', 'calendar', 'team'] : ['board', 'table', 'calendar', 'team'];
  let view = f.view || (isPhone ? 'list' : 'board');
  if (isPhone && view === 'table') view = 'list';
  if (!isPhone && view === 'list') view = 'table';
  if (!viewKeys.includes(view)) view = viewKeys[0];
  const defaultGroup = isPhone && view === 'list' ? 'due' : 'section';
  const group = GROUP_KEYS.includes(f.group) ? f.group : defaultGroup;
  const focus = FOCUS_KEYS.includes(f.focus) ? f.focus : '';
  const showDone = Boolean(f.done);
  const chips = { person: f.person, project: f.project, priority: f.priority, location: f.location };
  const chipsActive = FILTER_KEYS.some((key) => (f[key] || []).length > 0);

  const projectNameById = useMemo(
    () => Object.fromEntries(projects.map((project) => [project.id, projectLabel(project)])),
    [projects]
  );

  // ---- loading (each list ignores answers to older requests) ----
  const fetchDoneWeek = useCallback(async () => {
    const since = weekStartIso();
    const request = ++doneRequestRef.current;
    try {
      const response = await api.get('/tasks', {
        params: { page: 1, limit: DONE_LIST_LIMIT, status: 'Done', completed_since: since },
      });
      if (request !== doneRequestRef.current) return;
      setDoneTasks((response.data?.items || []).filter((task) => completedSince(task, since)));
    } catch {
      // "Show done" is a convenience; the board itself still works without it.
      if (request === doneRequestRef.current) setDoneTasks([]);
    } finally {
      if (request === doneRequestRef.current) setDoneWeekLoaded(true);
    }
  }, []);

  /** Done tasks of the last 8 weeks for Team load's on-time chart (not search-filtered, like the week's total). */
  const fetchDoneHistory = useCallback(async () => {
    const since = `${weekBounds(-7).start}T00:00:00+04:00`;
    const request = ++doneHistoryRequestRef.current;
    setDoneHistoryLoading(true);
    try {
      const response = await api.get('/tasks', {
        params: { page: 1, limit: 2000, status: 'Done', completed_since: since },
      });
      if (request !== doneHistoryRequestRef.current) return;
      setDoneHistory((response.data?.items || []).filter((task) => completedSince(task, since)));
    } catch {
      // The chart is extra: without it Team load still shows who carries what.
      if (request === doneHistoryRequestRef.current) setDoneHistory([]);
    } finally {
      if (request === doneHistoryRequestRef.current) setDoneHistoryLoading(false);
    }
  }, []);

  const fetchDoneMatches = useCallback(async (query = '') => {
    const request = ++doneMatchRequestRef.current;
    const needle = query.trim();
    if (!needle) {
      setDoneMatchIds(null);
      return;
    }
    const since = weekStartIso();
    try {
      const response = await api.get('/tasks', {
        params: { page: 1, limit: DONE_LIST_LIMIT, status: 'Done', completed_since: since, search: needle },
      });
      if (request !== doneMatchRequestRef.current) return;
      setDoneMatchIds(new Set((response.data?.items || []).filter((task) => completedSince(task, since)).map((task) => task.id)));
    } catch {
      if (request === doneMatchRequestRef.current) setDoneMatchIds(new Set());
    }
  }, []);

  const fetchTasks = useCallback(async (query = '', { quiet = false } = {}) => {
    const request = ++tasksRequestRef.current;
    if (!quiet) setRefreshing(true);
    try {
      const response = await api.get('/tasks/pending', { params: { page: 1, limit: 2000, search: query.trim() } });
      if (request === tasksRequestRef.current) setTasks(response.data?.items || []);
    } catch (error) {
      if (request === tasksRequestRef.current) notify.error('Could not load pending tasks', { description: getErrorMessage(error) });
    } finally {
      if (request === tasksRequestRef.current) setRefreshing(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    const query = String(searchValueRef.current || '').trim();
    const loadId = ++loadRequestRef.current; // the whole page load (lists, people, options, the loading state)
    const request = ++tasksRequestRef.current; // the task list (a later search refetch supersedes it)
    lastSearchRef.current = searchValueRef.current;
    setLoading(true);
    setLoadError('');
    try {
      const [tasksResponse, projectsResponse, membersResponse, optionsResponse] = await Promise.all([
        api.get('/tasks/pending', { params: { page: 1, limit: 2000, ...(query ? { search: query } : {}) } }),
        api.get('/projects'),
        api.get('/tasks/team-members'),
        api.get('/tasks/options'),
      ]);
      // Only the newest load may fill the page: an older answer (React StrictMode mounts twice in development,
      // or "Try again" while a load is still running) would otherwise end the loading state with an empty
      // board — "Tasks 0 · Nothing pending" — until the real answer lands.
      if (loadId !== loadRequestRef.current) return;
      if (request === tasksRequestRef.current) setTasks(tasksResponse.data?.items || []);
      setProjects(projectsResponse.data || []);
      setMembers(membersResponse.data || []);
      setOptions(optionsResponse.data || { sections: [], locations: [] });
    } catch (error) {
      if (loadId === loadRequestRef.current) setLoadError(getErrorMessage(error, 'Could not load tasks'));
    } finally {
      if (loadId === loadRequestRef.current) setLoading(false);
    }
    if (loadId !== loadRequestRef.current) return;
    fetchDoneWeek();
    fetchDoneMatches(query);
    setDoneHistoryEpoch((value) => value + 1); // the on-time chart's 8 weeks: fetched when shown (below)
  }, [fetchDoneMatches, fetchDoneWeek]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Team load's on-time chart is on Board (the card) and Team only: Table, Calendar and the phone List never
  // pay for 8 weeks of done tasks.
  const chartShown = view === 'board' || view === 'team';
  useEffect(() => {
    if (!chartShown || !doneHistoryEpoch || doneHistoryFetchedRef.current === doneHistoryEpoch) return;
    doneHistoryFetchedRef.current = doneHistoryEpoch;
    fetchDoneHistory();
  }, [chartShown, doneHistoryEpoch, fetchDoneHistory]);

  // Refetch (debounced) only when the search really changed since the last fetch.
  useEffect(() => {
    if (loading || f.search === lastSearchRef.current) return undefined;
    const timer = window.setTimeout(() => {
      lastSearchRef.current = f.search;
      fetchTasks(f.search);
      fetchDoneMatches(f.search);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [fetchDoneMatches, fetchTasks, loading, f.search]);

  // ---- what is shown ----
  // /tasks/pending leaves out only Done: cancelled tasks arrive too, and are closed work like done ones.
  const pendingTasks = useMemo(() => tasks.filter((task) => !isClosed(task)), [tasks]);
  const cancelledTasks = useMemo(() => tasks.filter(isCancelled), [tasks]);
  const shownDoneTasks = useMemo(
    () => (doneMatchIds ? doneTasks.filter((task) => doneMatchIds.has(task.id)) : doneTasks),
    [doneMatchIds, doneTasks]
  );
  const scoped = useMemo(() => applyTaskFilters(pendingTasks, chips), [pendingTasks, f.person, f.project, f.priority, f.location]); // eslint-disable-line react-hooks/exhaustive-deps -- chips is rebuilt each render
  const counters = useMemo(() => focusSummary(scoped, { user, fresh: freshIds }), [freshIds, scoped, user]);
  const focused = useMemo(
    () => (focus ? scoped.filter((task) => matchesFocus(task, focus, { user, fresh: freshIds })) : scoped),
    [focus, freshIds, scoped, user]
  );
  // "Show done": this week's done tasks and the cancelled ones (both faded, under their own headings).
  const doneScoped = useMemo(() => {
    const list = applyTaskFilters([...shownDoneTasks, ...cancelledTasks], chips);
    if (!focus) return list;
    return focus === 'mine' ? list.filter((task) => matchesFocus(task, 'mine', { user })) : [];
  }, [cancelledTasks, focus, shownDoneTasks, user, f.person, f.project, f.priority, f.location]); // eslint-disable-line react-hooks/exhaustive-deps
  const isWaiting = useCallback((task) => needsDetails(task) && !freshIds.has(task.id), [freshIds]);
  const needs = useMemo(() => focused.filter(isWaiting), [focused, isWaiting]);
  const boardTasks = useMemo(() => focused.filter((task) => !isWaiting(task)), [focused, isWaiting]);

  const sectionBoard = useMemo(() => buildLanes({
    tasks: boardTasks,
    doneTasks: doneScoped,
    configuredSections: options.sections,
    showEmpty,
    showDone,
  }), [boardTasks, doneScoped, options.sections, showDone, showEmpty]);
  const lanes = useMemo(() => {
    if (group === 'section') {
      return sectionBoard.lanes.map((lane) => ({ ...lane, key: lane.section, label: lane.section, prefill: { section: lane.section } }));
    }
    return groupTasks(boardTasks, group, { doneTasks: doneScoped, showDone, projectNameById });
  }, [boardTasks, doneScoped, group, projectNameById, sectionBoard, showDone]);

  const filterOptions = useMemo(() => buildFilterOptions(pendingTasks, {
    members,
    projectNameById,
    locations: options.locations || [],
    selected: chips,
  }), [members, options.locations, pendingTasks, projectNameById, f.person, f.project]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Team load (idea 10): the chip filters apply (a project chip = that project's team), focus does not ----
  const teamPeriod = f.load === 'next' ? 'next' : 'this';
  const loadTasks = useMemo(() => scoped.filter((task) => !isWaiting(task)), [isWaiting, scoped]);
  const waitingCount = scoped.length - loadTasks.length;
  const doneForLoad = useMemo(() => applyTaskFilters(doneTasks, chips), [doneTasks, f.person, f.project, f.priority, f.location]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useMemo(() => teamLoad(loadTasks, {
    doneTasks: doneForLoad,
    members,
    period: teamPeriod,
    needsDetails: waitingCount,
  }), [doneForLoad, loadTasks, members, teamPeriod, waitingCount]);
  const allOpenById = useMemo(() => (teamPeriod === 'next'
    ? Object.fromEntries(teamLoad(loadTasks, { members }).people.map((row) => [row.id, row.open]))
    : {}), [loadTasks, members, teamPeriod]);
  const onTime = useMemo(() => {
    // This week from doneTasks (kept in step with Done / Undo / Reopen), the 7 weeks before from the history.
    const monday = weekBounds(0).start;
    const earlier = doneHistory.filter((task) => isoDate(task.completed_at) < monday);
    return onTimeWeeks(applyTaskFilters([...doneTasks, ...earlier], chips));
  }, [doneHistory, doneTasks, f.person, f.project, f.priority, f.location]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedTaskId = f.task || null;
  const selectedTask = useMemo(
    () => tasks.find((task) => task.id === selectedTaskId)
      || doneTasks.find((task) => task.id === selectedTaskId)
      || extraTasks[selectedTaskId]
      || null,
    [doneTasks, extraTasks, selectedTaskId, tasks]
  );
  const paneOpen = Boolean(draft || selectedTask);
  const docked = wide && !isPhone;

  // /tasks?task=<id> for a task on none of the board's lists (closed weeks ago — a notification, an AI answer or a
  // search result): ask the server for it once (GET /tasks/{id}, same visibility rule) and keep it for the pane. A task
  // that is gone, or that this role may not open, clears the link with a message.
  const taskLinkRef = useRef({ asked: '', current: '' });
  taskLinkRef.current.current = selectedTaskId || '';
  useEffect(() => {
    // Waits for this week's done list too: a link to a task done this week is on it (no request needed).
    if (loading || !doneWeekLoaded || !selectedTaskId || selectedTask || draft || taskLinkRef.current.asked === selectedTaskId) return;
    const wanted = selectedTaskId;
    taskLinkRef.current.asked = wanted;
    api.get(`/tasks/${encodeURIComponent(wanted)}`)
      .then((response) => {
        if (response.data?.id === wanted) setExtraTasks((current) => ({ ...current, [wanted]: response.data }));
      })
      .catch((error) => {
        if (taskLinkRef.current.current !== wanted) return; // the person opened something else meanwhile
        notify.error('Could not open that task', { description: getErrorMessage(error, 'It may have been deleted.') });
        setF({ task: '' });
      });
  }, [loading, doneWeekLoaded, selectedTaskId, selectedTask, draft, setF]);

  // ---- open / close (each asks first while the pane holds unsaved typed work) ----
  const { confirmLeave } = useUnsavedChanges(paneOpen && Boolean(paneUnsaved), {
    title: `Discard ${paneUnsaved || 'your changes'}?`,
    description: paneUnsaved === 'this new task'
      ? 'It has not been created yet. If you continue, what you typed and picked is lost.'
      : 'It has not been saved yet. If you continue, it is lost.',
    confirmLabel: 'Discard',
    cancelLabel: 'Keep editing',
  });
  /** → true when the task is (now) open in the pane. */
  const openTask = useCallback(async (task) => {
    if (!draft && task.id === f.task) return true;
    if (!(await confirmLeave())) return false;
    setDraft(null);
    setF({ task: task.id });
    return true;
  }, [confirmLeave, draft, f.task, setF]);
  /** A history row: the task may be on none of the board's lists, so the board keeps it for the pane. */
  const openHistoryTask = useCallback((task) => {
    if (!task?.id) return Promise.resolve(false);
    setExtraTasks((current) => ({ ...current, [task.id]: task }));
    return openTask(task);
  }, [openTask]);
  const forgetExtra = (ids) => setExtraTasks((current) => {
    const gone = ids.filter((id) => current[id]);
    if (!gone.length) return current;
    const next = { ...current };
    gone.forEach((id) => { delete next[id]; });
    return next;
  });
  const closePane = useCallback(async () => {
    if (!(await confirmLeave())) return;
    setDraft(null);
    setFillMode(false);
    if (f.task) setF({ task: '' });
  }, [confirmLeave, f.task, setF]);
  const openNewTask = async (prefill = {}) => {
    if (readOnly) return;
    if (!(await confirmLeave())) return;
    if (f.task) setF({ task: '' });
    setDraft({ prefill, key: Date.now() });
  };

  // ---- changes ----
  const replaceTask = useCallback((updated) => {
    if (!updated?.id) return;
    const extra = Boolean(extraTasksRef.current[updated.id]);
    if (extra) setExtraTasks((current) => ({ ...current, [updated.id]: updated }));
    if (isDone(updated)) {
      setTasks((current) => current.filter((task) => task.id !== updated.id));
      setDoneTasks((current) => {
        if (current.some((task) => task.id === updated.id)) return current.map((task) => (task.id === updated.id ? updated : task));
        // A task closed before this Monday (opened from a person's history) is not one of "this week's".
        return extra && !completedSince(updated, weekStartIso()) ? current : [updated, ...current];
      });
      return;
    }
    setTasks((current) => {
      if (current.some((task) => task.id === updated.id)) return current.map((task) => (task.id === updated.id ? updated : task));
      // Opened from a history while a search hides it: it stays off the searched board (the pane keeps it).
      if (extra && String(searchValueRef.current || '').trim()) return current;
      return [...current, updated];
    });
    setDoneTasks((current) => current.filter((task) => task.id !== updated.id));
  }, []);

  const createTask = async (section, name) => {
    try {
      const response = await api.post('/tasks', {
        name,
        section: sectionValue(section),
        status: 'Open',
        priority: 'Medium',
      });
      markFresh(response.data.id);
      setTasks((current) => [...current, response.data]);
      notify.success(`“${name}” added to ${section}`);
    } catch (error) {
      notify.error('Could not create the task', { description: getErrorMessage(error) });
      throw error;
    }
  };

  /** The pane's "Create task": nothing is saved before the task has a name. */
  const createFromPane = async (payload) => {
    try {
      const response = await api.post('/tasks', payload);
      markFresh(response.data.id);
      setTasks((current) => [...current, response.data]);
      setDraft(null);
      setF({ task: response.data.id });
      notify.success(`“${payload.name}” created`);
      return response.data;
    } catch (error) {
      notify.error('Could not create the task', { description: getErrorMessage(error) });
      return null;
    }
  };

  const markDone = async (task) => {
    // Done closes the pane of that task: typed work in it would be lost.
    if (task.id === selectedTaskId && !draft && !(await confirmLeave())) return;
    const previous = {
      status: normalizeStatus(task.status),
      blocked_reason: task.blocked_reason || '',
    };
    try {
      const response = await api.post(`/tasks/${task.id}/done`);
      const doneTask = response.data || { ...task, status: 'Done' };
      const onBoard = tasks.some((item) => item.id === task.id);
      setTasks((current) => current.filter((item) => item.id !== task.id));
      setDoneTasks((current) => [doneTask, ...current.filter((item) => item.id !== task.id)]);
      if (onBoard) setDoneMatchIds((current) => (current ? new Set([...current, task.id]) : current)); // it matched: it was on screen
      if (extraTasksRef.current[task.id]) setExtraTasks((current) => ({ ...current, [task.id]: doneTask }));
      if (selectedTaskId === task.id) setF({ task: '' });
      notify.success(`“${task.name}” marked done`, {
        undo: async () => {
          if (doneTask.undo_id) {
            // Undo exactly this change (a newer change by someone else is never rolled back).
            await api.post(`/undo/entries/${encodeURIComponent(doneTask.undo_id)}`);
          } else {
            await api.patch(`/tasks/${task.id}`, previous.status === 'Blocked' ? previous : { status: previous.status });
          }
          setDoneTasks((current) => current.filter((item) => item.id !== task.id));
          await fetchTasks(searchValueRef.current, { quiet: true });
        },
        undoneMessage: `“${task.name}” is back on the board`,
      });
    } catch (error) {
      notify.error('Could not mark the task done', { description: getErrorMessage(error) });
    }
  };

  const reopenTask = async (task) => {
    try {
      const response = await api.patch(`/tasks/${task.id}`, { status: 'Open' });
      replaceTask(response.data);
      notify.success(`“${task.name}” reopened`);
    } catch (error) {
      notify.error('Could not reopen the task', { description: getErrorMessage(error) });
    }
  };

  /**
   * PATCH from the card's quick panels, the calendar and the pane. `message` → a success toast (Undo is added
   * by the shell). The pane passes `throwOnError` and shows the error itself ("Couldn’t save — …").
   */
  const updateTask = async (task, payload, { message, throwOnError = false } = {}) => {
    if (payload?.status === 'Done') {
      await markDone(task);
      return task;
    }
    try {
      const response = await api.patch(`/tasks/${task.id}`, payload);
      replaceTask(response.data);
      if (message) notify.success(message);
      return response.data;
    } catch (error) {
      if (throwOnError) throw error;
      notify.error('Could not update the task', { description: getErrorMessage(error) });
      return null;
    }
  };

  /** → true when moved (the pane shows "Saved" then). */
  const moveTask = async (task, payload, { message } = {}) => {
    try {
      const response = await api.patch(`/tasks/${task.id}/move`, payload);
      replaceTask(response.data);
      if (message) notify.success(message);
      return true;
    } catch (error) {
      notify.error('Could not move the task', { description: getErrorMessage(error) });
      await fetchTasks(searchValueRef.current, { quiet: true });
      return false;
    }
  };

  const moveToSection = async (task, section) => {
    if (sectionOf(task) === section) return true;
    return moveTask(task, { section: sectionValue(section) }, { message: `“${task.name}” moved to ${section}` });
  };

  const deleteTask = async (task) => {
    const attachmentCount = (task.attachments || []).length;
    const ok = await confirm({
      title: `Delete “${task.name}”?`,
      subtitle: [sectionOf(task), normalizeStatus(task.status)].join(' · '),
      description: attachmentCount
        ? `The task and its ${attachmentCount === 1 ? 'attached file is' : `${attachmentCount} attached files are`} removed for everyone.`
        : 'The task is removed for everyone.',
      tone: 'danger',
      confirmLabel: 'Delete task',
      // The server keeps the files aside, so Undo brings the task back with working attachments.
      note: attachmentCount
        ? 'You can undo this right after from the toast or from Recent changes — the files come back too.'
        : 'You can undo this right after from the toast or from Recent changes.',
      onConfirm: () => api.delete(`/tasks/${task.id}`),
    });
    if (!ok) return;
    setTasks((current) => current.filter((item) => item.id !== task.id));
    setDoneTasks((current) => current.filter((item) => item.id !== task.id));
    forgetExtra([task.id]);
    if (selectedTaskId === task.id) setF({ task: '' });
    notify.success(`“${task.name}” deleted`);
  };

  /** Bulk bar: one request, one undo entry (the shell's toast undoes the whole change). */
  const bulkUpdate = async (selected, payload, message) => {
    if (!selected.length) return;
    try {
      const response = await api.post('/tasks/bulk-update', { task_ids: selected.map((task) => task.id), ...payload });
      (response.data?.items || []).forEach(replaceTask);
      notify.success(message);
    } catch (error) {
      notify.error('Could not update the tasks', { description: getErrorMessage(error) });
    }
  };
  const bulkDelete = async (selected) => {
    const ids = selected.map((task) => task.id);
    if (!ids.length) return;
    try {
      await api.post('/tasks/bulk-delete', { task_ids: ids });
      setTasks((current) => current.filter((task) => !ids.includes(task.id)));
      setDoneTasks((current) => current.filter((task) => !ids.includes(task.id)));
      forgetExtra(ids);
      setTableSelection((current) => current.filter((id) => !ids.includes(id)));
      if (ids.includes(selectedTaskId)) setF({ task: '' });
      notify.success(`${plural(ids.length, 'task')} deleted`);
    } catch (error) {
      notify.error('Could not delete the tasks', { description: getErrorMessage(error) });
    }
  };

  /**
   * Team load: a card dropped on a person (or "Assign to…" in the Team view). From another person's row it
   * moves to the new person; otherwise the person is added. One PATCH → the shell's toast offers Undo.
   */
  const assignToPerson = async (taskId, person, { fromPersonId } = {}) => {
    const task = pendingTasks.find((item) => item.id === taskId);
    if (!task || !person?.id) return;
    const payload = assignPatch(task, person, { fromPersonId });
    if (!payload) {
      notify.info(`“${task.name}” is already with ${person.name}`);
      return;
    }
    const from = fromPersonId ? taskAssignees(task).find((item) => item.id === fromPersonId) : null;
    await updateTask(task, payload, {
      message: from ? `“${task.name}” moved from ${from.name} to ${person.name}` : `“${task.name}” assigned to ${person.name}`,
    });
  };
  const showPersonOnBoard = (personId) => {
    setF({ person: [personId], focus: '', view: '' }, { replace: false });
    rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }); // back up to the filtered board
  };

  const moveDates = (task, next, day) => {
    updateTask(task, next, { message: `“${task.name}” now due ${formatDate(next.due_date, { year: 'auto' })}${next.start_date ? ` (from ${formatDate(next.start_date, { year: 'auto' })})` : ''}` });
    return day;
  };

  const addSection = async (value) => {
    try {
      const response = await api.post('/tasks/options', { option_type: 'section', value });
      setOptions(response.data || options);
      notify.success(`Section “${value}” added`);
      return true;
    } catch (error) {
      notify.error('Could not add the section', { description: getErrorMessage(error) });
      return false;
    }
  };

  const changeSection = async (section, newName) => {
    if (readOnly) return false;
    if (newName === undefined && !(await confirm({
      title: `Delete section “${section}”?`,
      description: 'All tasks in this section, including completed tasks, will move to No Section. Tasks, notes and attachments are kept.',
      confirmLabel: 'Delete section',
      tone: 'danger',
    }))) return false;
    if (!(await confirmLeave())) return false;
    try {
      const response = newName === undefined
        ? await api.delete('/tasks/sections', { data: { name: section } })
        : await api.patch('/tasks/sections', { name: section, new_name: newName });
      setOptions({ sections: response.data.sections, locations: response.data.locations });
      setDraft(null);
      setFillMode(false);
      setExtraTasks({});
      if (f.task) setF({ task: '' });
      await fetchData();
      notify.success(newName === undefined ? `Section “${section}” deleted` : `Section renamed to “${newName}”`, {
        ...(response.data.undo_id ? {
          undo: async () => {
            await api.post(`/undo/entries/${encodeURIComponent(response.data.undo_id)}`);
            await fetchData();
          },
          undoneMessage: 'Section change undone',
        } : {}),
      });
      return true;
    } catch (error) {
      notify.error('Could not change the section', { description: getErrorMessage(error) });
      return false;
    }
  };

  // ---- drag & drop (mouse, Section grouping); "Move to section…" in ⋯ is the keyboard / touch path ----
  const draggedTask = drag.taskId ? pendingTasks.find((task) => task.id === drag.taskId) : null;
  const handleDragStart = (event, task) => {
    setDrag({ taskId: task.id, section: null, beforeId: null });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', task.id);
  };
  const clearDrag = () => setDrag({ taskId: null, section: null, beforeId: null });
  const handleCardDragOver = (event, task) => {
    // Over a done card the lane takes it (drop at the end). Outside the Section grouping cards only drag
    // onto a person in Team load, never onto another card.
    if (!drag.taskId || isDone(task) || group !== 'section') return;
    event.stopPropagation();
    if (task.id === drag.taskId) {
      if (drag.section !== null || drag.beforeId !== null) setDrag((current) => ({ ...current, section: null, beforeId: null }));
      return;
    }
    event.preventDefault();
    const section = sectionOf(task);
    if (drag.section !== section || drag.beforeId !== task.id) setDrag((current) => ({ ...current, section, beforeId: task.id }));
  };
  const handleLaneDragOver = (event, section) => {
    if (!drag.taskId) return;
    event.preventDefault();
    if (drag.section !== section || drag.beforeId !== null) setDrag((current) => ({ ...current, section, beforeId: null }));
  };
  const handleDropBefore = (event, beforeTask) => {
    if (isDone(beforeTask) || group !== 'section') return;
    event.preventDefault();
    event.stopPropagation();
    const task = draggedTask;
    clearDrag();
    if (!task || task.id === beforeTask.id) return;
    moveTask(task, { section: beforeTask.section || '', before_id: beforeTask.id });
  };
  /** The pointer reached Team load: no lane is the drop target any more. */
  const dragIntoTeam = () => {
    if (drag.section !== null || drag.beforeId !== null) setDrag((current) => ({ ...current, section: null, beforeId: null }));
  };
  const handleLaneDrop = (event, section) => {
    event.preventDefault();
    const task = draggedTask;
    clearDrag();
    if (!task) return;
    moveTask(task, { section: sectionValue(section) });
  };

  const cardProps = {
    projectNameById,
    members,
    sections: sectionBoard.allSections,
    onOpen: openTask,
    onDone: markDone,
    onReopen: reopenTask,
    onPatch: updateTask,
    onMove: moveToSection,
    onDelete: deleteTask,
    onDragStart: handleDragStart,
    onDragEnd: clearDrag,
    onDragOver: handleCardDragOver,
    onDrop: handleDropBefore,
    readOnly,
  };

  // ---- table ----
  const columns = useTaskColumns({ projectNameById, onOpen: openTask, onDone: markDone, onReopen: reopenTask, onDelete: deleteTask, readOnly });
  const columnState = useColumnVisibility('tasks', columns);
  const tableSort = parseSortParam(f.sort) || DEFAULT_SORT;
  const tableRows = useMemo(() => (showDone ? [...focused, ...doneScoped] : focused), [doneScoped, focused, showDone]);

  // ---- words ----
  const pendingCount = pendingTasks.length;
  const doneCount = doneTasks.length;
  const searching = Boolean(f.search.trim());
  const doneMatchCount = shownDoneTasks.length;
  const visibleDone = showDone ? doneMatchCount + cancelledTasks.length : 0;
  const noSearchResults = searching && pendingCount === 0 && visibleDone === 0 && !refreshing;
  const filtering = chipsActive || Boolean(focus);
  const allNeeds = pendingTasks.filter(isWaiting).length;
  const onBoard = pendingCount - allNeeds;

  let subtitle = 'Pending work across all projects';
  if (!loading && searching) {
    subtitle = `${plural(pendingCount, 'pending task')} ${pendingCount === 1 ? 'matches' : 'match'} “${f.search.trim()}”`;
  } else if (!loading && filtering) {
    subtitle = `${focused.length} of ${plural(pendingCount, 'pending task')} match${focused.length === 1 ? 'es' : ''} ${focus ? `“${FOCUS_OPTIONS.find((item) => item.key === focus)?.label}”` : 'these filters'}`;
  } else if (!loading) {
    subtitle = `Pending work across all projects — ${onBoard} on the board${allNeeds ? `, ${allNeeds} ${allNeeds === 1 ? 'needs' : 'need'} details` : ''}`;
  }

  const clearFilters = () => setF({ person: [], project: [], priority: [], location: [], focus: '' });
  const nextUp = (() => {
    if (!selectedTask || !fillMode) return null;
    const next = needs.find((task) => task.id !== selectedTask.id);
    return next ? { label: next.name, onOpen: () => openTask(next) } : null;
  })();

  // ---- toolbar ----
  const viewOptions = (isPhone
    ? [
      { key: 'list', label: viewLabel(ListChecks, 'List', { compact: true }), testId: 'tasks-view-list' },
      { key: 'board', label: viewLabel(Columns3, 'Board', { compact: true }), testId: 'tasks-view-board' },
      { key: 'calendar', label: viewLabel(CalendarDays, 'Calendar', { compact: true }), testId: 'tasks-view-calendar' },
      { key: 'team', label: viewLabel(Users, 'Team', { compact: true }), title: 'Team load — who carries what', testId: 'tasks-view-team' },
    ]
    : [
      { key: 'board', label: viewLabel(Columns3, 'Board'), count: loading ? undefined : boardTasks.length, testId: 'tasks-view-board' },
      { key: 'table', label: viewLabel(Table2, 'Table'), testId: 'tasks-view-table' },
      { key: 'calendar', label: viewLabel(CalendarDays, 'Calendar'), testId: 'tasks-view-calendar' },
      { key: 'team', label: viewLabel(Users, 'Team'), title: 'Team load — who carries what', testId: 'tasks-view-team' },
    ]);
  const setView = (next) => {
    const isDefault = next === (isPhone ? 'list' : 'board');
    setF({ view: isDefault ? '' : next }, { replace: false });
  };

  const groupSelect = view === 'calendar' || view === 'team' ? null : (
    <Select value={group} onValueChange={(value) => setF({ group: value === defaultGroup ? '' : value })}>
      <SelectTrigger
        className="h-[30px] w-auto min-w-[150px] gap-1.5 rounded-full border-line bg-surface px-3 text-[12.5px] font-semibold text-ink shadow-card"
        aria-label="Group by"
        data-testid="task-group-by"
      >
        <span className="text-ink-3">Group by:</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {GROUP_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const showDoneChip = (
    <button
      type="button"
      onClick={() => setF({ done: !showDone })}
      aria-pressed={showDone}
      disabled={loading}
      title="This week’s done tasks and the cancelled ones, faded at the end of their lanes"
      className={cn(
        'inline-flex h-[30px] items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
        showDone
          ? 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink'
          : 'border-line bg-surface text-ink-2 shadow-card hover:bg-surface-hover'
      )}
      data-testid="task-show-done"
    >
      {showDone ? <EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> : <Eye className="h-3.5 w-3.5" aria-hidden="true" />}
      {showDone ? 'Hide done' : 'Show done'}
      <span className="font-medium text-ink-3">
        {/* The week's total is never search-filtered; while searching, how many of them match comes first. */}
        · {doneMatchIds && doneCount ? `${doneMatchCount} of ${doneCount}` : doneMatchIds ? doneMatchCount : doneCount}
        <span className="max-[400px]:sr-only"> this week</span>
      </span>
    </button>
  );

  const filterBar = (
    <FilterBar
      className="min-w-0 flex-1"
      search={{
        value: f.search,
        onChange: (search) => setF({ search }),
        placeholder: 'Search tasks, people…',
        label: 'Search tasks, comments, projects, people and locations',
        testId: 'task-search-input',
        className: 'sm:w-[260px]',
      }}
      filters={[
        { key: 'person', label: 'Person', options: filterOptions.person, value: f.person, onChange: (person) => setF({ person }), testId: 'task-filter-person', popoverClassName: 'w-[260px]' },
        { key: 'project', label: 'Project', options: filterOptions.project, value: f.project, onChange: (project) => setF({ project }), testId: 'task-filter-project', popoverClassName: 'w-[280px]' },
        { key: 'priority', label: 'Priority', options: filterOptions.priority, value: f.priority, onChange: (priority) => setF({ priority }), testId: 'task-filter-priority' },
        { key: 'location', label: 'Location', options: filterOptions.location, value: f.location, onChange: (location) => setF({ location }), testId: 'task-filter-location' },
      ]}
      onClearAll={() => setF({ search: '', person: [], project: [], priority: [], location: [], focus: '' })}
      clearable={searching || filtering}
      testId="tasks-filter-bar"
    />
  );

  // ---- Team load card (under the board) / Team view (idea 10) ----
  const teamView = (mode) => (
    <TeamLoad
      mode={mode}
      load={load}
      allOpenById={allOpenById}
      weeks={onTime}
      weeksLoading={doneHistoryLoading}
      period={teamPeriod}
      onPeriodChange={(next) => setF({ load: next === 'next' ? 'next' : '' })}
      filtered={chipsActive || searching}
      searching={searching}
      members={members}
      projectNameById={projectNameById}
      externalDragTaskId={drag.taskId}
      onDragIntoTeam={dragIntoTeam}
      onAssign={assignToPerson}
      onOpenTask={openTask}
      onOpenPerson={(row) => { setHistoryMember({ id: row.id, name: row.name, role: row.role }); setHistoryOpen(true); }}
      onShowPerson={showPersonOnBoard}
      onShowNeedsDetails={() => setF({ focus: 'details', view: '' }, { replace: false })}
      onOpenTeamView={() => setView('team')}
      onBackToBoard={() => setView('board')}
      readOnly={readOnly}
    />
  );

  // ---- content ----
  let content;
  if (loading) {
    content = <BoardSkeleton />;
  } else if (loadError) {
    content = (
      <div className="rounded-card border border-line bg-surface shadow-card">
        <EmptyState layout="inline" tone="red" icon={RefreshCw} title="Couldn’t load tasks" text={loadError} titleAs="h2" action={<Button size="sm" onClick={fetchData}>Try again</Button>} />
      </div>
    );
  } else if (noSearchResults) {
    content = (
      <div className="rounded-card border border-line bg-surface shadow-card" data-testid="tasks-no-results">
        <EmptyState
          icon={SearchX}
          title={`No tasks match “${f.search.trim()}”`}
          text="Search looks at task names, comments, notes, projects, people and locations."
          titleAs="h2"
          action={<Button variant="outline" size="sm" onClick={() => setF({ search: '' })}>Clear search</Button>}
        />
      </div>
    );
  } else if (view === 'team') {
    // The Team view keeps showing (with the on-time chart) even when nothing is open.
    content = teamView('full');
  } else if (!pendingCount && !visibleDone && view !== 'calendar' && !(view === 'board' && showEmpty)) {
    content = (
      <div className="rounded-card border border-line bg-surface shadow-card" data-testid="tasks-empty">
        <EmptyState
          icon={ListChecks}
          title="Nothing pending"
          text={doneCount
            ? `All caught up — ${doneCount} ${doneCount === 1 ? 'task was' : 'tasks were'} done this week.`
            : readOnly ? 'No open tasks right now.' : 'Add a task with New task, or open a section to add one there.'}
          titleAs="h2"
          action={(
            <>
              {doneCount ? <Button variant="outline" size="sm" onClick={() => setF({ done: true })}>Show done</Button> : null}
              <Button variant="outline" size="sm" onClick={() => setShowEmpty(true)}>Show all sections</Button>
            </>
          )}
        />
      </div>
    );
  } else if (filtering && !focused.length && !(showDone && doneScoped.length)) {
    content = (
      <div className="rounded-card border border-line bg-surface shadow-card" data-testid="tasks-no-filter-results">
        <EmptyState
          icon={SearchX}
          title={focus ? `Nothing for “${FOCUS_OPTIONS.find((item) => item.key === focus)?.label}”` : 'No tasks match these filters'}
          text={focus ? 'Good news — or try other filters.' : 'Try removing a filter.'}
          titleAs="h2"
          action={<Button variant="outline" size="sm" onClick={clearFilters}>Show all tasks</Button>}
        />
      </div>
    );
  } else if (view === 'table') {
    content = (
      <TaskTable
        tasks={tableRows}
        columns={columns}
        columnState={columnState}
        group={group}
        sections={sectionBoard.allSections}
        projectNameById={projectNameById}
        selectedTaskId={selectedTaskId}
        sort={tableSort}
        onSortChange={(next) => {
          const value = formatSortParam(next);
          setF({ sort: value === formatSortParam(DEFAULT_SORT) ? '' : value });
        }}
        selectedKeys={tableSelection}
        onSelectionChange={setTableSelection}
        members={members}
        projects={projects}
        onOpen={openTask}
        onAdd={openNewTask}
        onBulkUpdate={bulkUpdate}
        onBulkDelete={bulkDelete}
        readOnly={readOnly}
        pagingKey={JSON.stringify([f.search, f.focus, f.person, f.project, f.priority, f.location, group])}
      />
    );
  } else if (view === 'calendar') {
    content = (
      <TaskCalendar
        tasks={focused}
        doneTasks={showDone ? doneScoped : []}
        mode={f.cal === 'month' ? 'month' : 'week'}
        onModeChange={(mode) => setF({ cal: mode })}
        anchor={f.at}
        onAnchorChange={(at) => setF({ at })}
        selectedTaskId={selectedTaskId}
        onOpen={openTask}
        onMoveDates={moveDates}
        isPhone={isPhone}
        readOnly={readOnly}
      />
    );
  } else {
    const showNeedsCard = needs.length && (view === 'board' || focus === 'details');
    const lanesBlock = lanes.length ? (
      view === 'list' ? (
        <TaskList
          lanes={lanes}
          cardProps={{ ...cardProps, selectedTaskId }}
          total={boardTasks.length}
          doneCount={doneCount}
          showDone={showDone}
          onToggleDone={() => setF({ done: !showDone })}
        />
      ) : (
        <TaskLanes
          lanes={lanes}
          sectionMode={group === 'section'}
          readOnly={readOnly}
          dragToPeople={!isPhone}
          drag={drag}
          onCreate={createTask}
          onAdd={openNewTask}
          onLaneDragOver={handleLaneDragOver}
          onLaneDrop={handleLaneDrop}
          onDragEnd={clearDrag}
          cardProps={{ ...cardProps, selectedTaskId }}
        />
      )
    ) : focus === 'details' ? null : (
      <p className="rounded-card border border-dashed border-[color:var(--line-dashed)] px-4 py-6 text-center text-[13px] text-ink-3" data-testid="tasks-board-only-needs">
        Every pending task still needs its details — fill them in below.
      </p>
    );
    content = (
      <div className="flex flex-col gap-4">
        {view === 'list' && needs.length && focus !== 'details' ? (
          <div className="flex items-center gap-3 rounded-card border border-status-amber-bg bg-status-amber-bg px-3.5 py-3" data-testid="tasks-needs-details-callout">
            <span className="ui-icon-tile tone-amber h-9 w-9 bg-surface" aria-hidden="true"><Inbox className="h-[17px] w-[17px]" /></span>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-bold text-ink">{plural(needs.length, 'task')} need details</div>
              <div className="truncate text-[12.5px] text-status-amber-ink" data-testid="tasks-needs-details-names">
                {summarizeNames(needs.map((task) => task.name))}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setF({ focus: 'details' })}
              className={cn('h-9 px-3.5', TAP_AREA)}
            >
              Review
            </Button>
          </div>
        ) : null}
        {lanesBlock}
        {showNeedsCard ? (
          <NeedsDetailsCard
            tasks={needs}
            selectedTaskId={selectedTaskId}
            members={members}
            projects={projects}
            sections={sectionBoard.allSections}
            onOpen={openTask}
            onFillIn={async (task) => { if (await openTask(task)) setFillMode(true); }}
            onDelete={deleteTask}
            onBulkUpdate={bulkUpdate}
            onBulkDelete={bulkDelete}
            readOnly={readOnly}
          />
        ) : null}
      </div>
    );
  }

  // The Show done chip stays even when a search finds no pending task (it may still find done ones).
  const strip = loading || loadError ? null : (
    <div className="flex min-h-[30px] flex-wrap items-center gap-x-3 gap-y-2">
      {view === 'team' ? (
        <span className="text-[12.5px] text-ink-2" data-testid="tasks-team-summary">
          {plural(load.people.length, 'person').replace(/persons$/, 'people')} with work
          {' · '}{load.unassigned.open} unassigned on the board
          {/* Tasks waiting for details have no person either: say how many, so "0 unassigned" never reads wrong. */}
          {load.unassigned.needsDetails
            ? ` · ${load.unassigned.needsDetails} ${load.unassigned.needsDetails === 1 ? 'needs' : 'need'} details`
            : null}
          {readOnly ? null : <span className="text-ink-3 [@media(hover:none)]:hidden"> · drag a task onto a person to assign it</span>}
        </span>
      ) : noSearchResults || focus === 'details' ? null : view === 'board' && group === 'section' ? (
        <span className="text-[12.5px] text-ink-2" data-testid="task-section-summary">
          {sectionBoard.activeCount} of {sectionBoard.totalCount} sections
          {sectionBoard.emptyCount || showEmpty ? (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => setShowEmpty((value) => !value)}
                className="rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-pressed={showEmpty}
                data-testid="task-toggle-empty-sections"
              >
                {showEmpty ? 'Hide empty' : `Show ${sectionBoard.emptyCount} empty`}
              </button>
            </>
          ) : null}
        </span>
      ) : view === 'board' || view === 'list' ? (
        <span className="text-[12.5px] text-ink-2">
          Grouped by {GROUP_OPTIONS.find((item) => item.value === group)?.label.toLowerCase()}
          {isPhone ? (
            <>
              {' · '}
              {chipsActive ? 'filtered' : 'everyone'}
              {' · '}
              <button
                type="button"
                onClick={() => setPhoneFilters((value) => !value)}
                className={cn('rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', TAP_AREA)}
                aria-expanded={phoneFilters}
                data-testid="tasks-phone-filters-toggle"
              >
                {phoneFilters ? 'Done' : 'Change'}
              </button>
            </>
          ) : ` · ${plural(lanes.length, 'lane')}`}
        </span>
      ) : view === 'table' ? (
        <span className="text-[12.5px] text-ink-2">
          {plural(tableRows.length, 'task')} · click a row to open it
          {group === 'person' || group === 'project' ? (
            <span className="text-ink-3" data-testid="tasks-table-group-note">
              {' · '}each task once, under its first {group === 'person' ? 'person' : 'project'}
            </span>
          ) : null}
        </span>
      ) : null}
      {focus && view !== 'team' ? (
        <span className="text-[12.5px] text-ink-2">
          Showing “{FOCUS_OPTIONS.find((item) => item.key === focus)?.label}” ·{' '}
          <button type="button" onClick={() => setF({ focus: '' })} className="font-semibold text-brand-ink hover:underline" data-testid="task-focus-clear">
            Show all
          </button>
        </span>
      ) : null}
      {!noSearchResults && (view === 'board' || view === 'list') ? <StatusLegend /> : null}
      <span className="flex-1" />
      {noSearchResults || (isPhone && !phoneFilters) ? null : groupSelect}
      {view === 'table' && !noSearchResults ? <ColumnsMenu state={columnState} /> : null}
      {(view !== 'list' && view !== 'team') || noSearchResults ? showDoneChip : null}
    </div>
  );

  const pane = paneOpen ? (
    <TaskPane
      key={draft ? `new-${draft.key}` : selectedTask?.id}
      task={draft ? null : selectedTask}
      isNew={Boolean(draft)}
      prefill={draft?.prefill}
      docked={docked}
      members={members}
      projects={projects}
      options={options}
      projectNameById={projectNameById}
      user={user}
      onClose={closePane}
      onPatch={updateTask}
      onMove={moveToSection}
      onDone={markDone}
      onReopen={reopenTask}
      onDelete={deleteTask}
      onCreate={createFromPane}
      onTaskChanged={replaceTask}
      onOptionsChanged={(next) => setOptions(next || options)}
      onMembersChanged={(member) => setMembers((current) => (current.some((item) => item.id === member.id) ? current : [...current, member]))}
      onDirtyChange={setPaneUnsaved}
      nextUp={nextUp}
      readOnly={readOnly}
    />
  ) : null;

  return (
    // The test id appears once the board has loaded (tests and e2e wait for it before interacting).
    // Phones: room at the end so the New task button never covers the last card.
    <div ref={rootRef} data-testid={loading ? 'pending-tasks-loading' : 'pending-tasks-board'} className="space-y-4 max-sm:pb-20">
      <PageHeader
        title="Tasks"
        count={loading ? undefined : pendingCount}
        subtitle={subtitle}
        actions={isPhone ? null : (
          <>
            <SectionsMenu
              sections={sectionBoard.allSections}
              counts={pendingTasks.reduce((map, task) => map.set(sectionOf(task), (map.get(sectionOf(task)) || 0) + 1), new Map())}
              showEmpty={showEmpty}
              onToggleEmpty={() => setShowEmpty((value) => !value)}
              onAdd={readOnly ? null : addSection}
              onRename={readOnly ? null : (section, name) => changeSection(section, name)}
              onDelete={readOnly ? null : (section) => changeSection(section)}
            />
            {readOnly ? null : (
              <Button type="button" onClick={() => openNewTask({})} data-testid="add-task-btn">
                <Plus aria-hidden="true" /> New task
              </Button>
            )}
          </>
        )}
      />

      <TaskFocusCounters
        summary={counters}
        value={focus}
        // A counter filters the board: from the Team view it goes back to the board.
        onChange={(next) => setF(view === 'team' ? { focus: next, view: '' } : { focus: next })}
        loading={loading}
        isPhone={isPhone}
      />

      <div className="flex flex-wrap items-start gap-x-2.5 gap-y-2">
        <Segmented
          ariaLabel="View"
          value={view}
          onChange={setView}
          options={viewOptions}
          fullWidth={isPhone}
          className={isPhone ? PHONE_SEGMENTS : undefined}
          testId="tasks-view-switch"
        />
        {!isPhone ? <span className="mt-[7px] hidden h-5 w-px bg-line md:block" aria-hidden="true" /> : null}
        {!isPhone || phoneFilters || searching || chipsActive ? filterBar : null}
        {isPhone && !phoneFilters && !searching && !chipsActive && view !== 'list' ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setPhoneFilters(true)} className={TAP_AREA}>
            <SlidersHorizontal aria-hidden="true" /> Filters
          </Button>
        ) : null}
      </div>

      {strip}

      {isPhone && view === 'list' && !loading && lanes.length && !readOnly ? (
        <p className="text-[12px] text-ink-3">Swipe left = Done · swipe right = Tomorrow · Undo in the toast</p>
      ) : null}

      <div className={cn('grid min-w-0 items-start gap-4', paneOpen && docked && 'grid-cols-[minmax(0,1fr)_400px]')}>
        <div className="min-w-0">{content}</div>
        {docked ? pane : null}
      </div>
      {!docked ? pane : null}

      {view === 'board' && !loading && !loadError && !noSearchResults ? teamView('card') : null}

      <TeamMemberTaskHistory
        member={historyMember}
        open={historyOpen && Boolean(historyMember)}
        onOpenChange={setHistoryOpen}
        projectNameById={projectNameById}
        onOpenTask={openHistoryTask}
        onShowOnBoard={(member) => showPersonOnBoard(member.id)}
      />

      {isPhone && !readOnly ? (
        <button
          type="button"
          onClick={() => openNewTask({})}
          className="fixed bottom-[calc(16px+var(--app-bottom-nav-height,0px))] right-4 z-30 grid h-14 w-14 place-items-center rounded-2xl bg-brand text-brand-fg shadow-overlay focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="New task"
          data-testid="add-task-btn"
        >
          <Plus className="h-6 w-6" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
